<template lang="pug">
teacher-list.teacher-management(
  ref='template',
  fullNamePlaceholder='氏名',
  branchNamePlaceholder='教室名',
  modalTitle='ユーザー登録（先生・講師）',
  modalPasswordPlaceholder='パスワード',
  modalPasswordConfirmPlaceholder='パスワード',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :tabHeadingEvent='tabHeadingEvent',
  :authorityPlaceholder='authorityPlaceholder',
  :searchEvent='searchEvent',
  :qrOutputEvent='qrOutputEvent',
  :downloadEvent='downloadEvent',
  :sort='sort',
  :teacherDatas='teacherDatas',
  :setEditDataEvent='setEditDataEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  :teacherEditData='teacherEditData',
  :modalBranchSelectOptions='modalBranchSelectOptions',
  :modalKeepButtonEvent='modalKeepButtonEvent',
  :modalErrorMessages='modalErrorMessages',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#searchButtonText) 絞り込む
  template(#qrOutputIconLabel) QRコード
  template(#downloadIconLabel) ダウンロード
  template(#userNameHeader) 氏名
  template(#bcbtCodeHeader) ID
  template(#branchNameHeader) 教室
  template(#roleNameHeader) 権限
  template(#editHeader) 編集
  template(#popupEditText) 編集
  template(#modalRoleNameLabel) 権限（編集不可）
  template(#modalBranchNameDisabledLabel) 教室名（編集不可）
  template(#modalUserNameLabel) 氏名（編集不可）
  template(#modalPasswordLabel) パスワード
  template(#modalPasswordLabelAnnotation) ※変更がない場合は空白
  template(#modalPasswordConfirmLabel) パスワード
  template(#modalPasswordConfirmLabelAnnotation) ※変更する場合の再確認用
  template(#modalKeepText) 保存
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import ManagersApi from '@/mixins/api/ManagersApi'
import BranchesApi from '@/mixins/api/BranchesApi'
import TeacherList from '@/components/templates/manager/TeacherList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import {
  TeacherListType,
  TeacherListSortType,
  TeacherEditType,
  TeacherSearchParamsType,
} from '@/models/teacherListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { SortSettings } from '@/const/SortSettings'
import { ManagerInputUtils } from '@/utils/ManagerInputUtils'
import { TeacherManagementUtils } from '@/utils/TeacherManagementUtils'

/**
 * 塾管理者・先生・講師管理画面
 */
@Component({
  components: {
    TeacherList,
  },
})
export default class TeacherManagement extends Mixins(LoginMethods, ManagersApi, BranchesApi) {
  // テンプレートへのアクセス
  @Ref() template!: TeacherList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'ユーザー管理',
    },
  ]

  // 塾管理者権限
  private userRole: UserRoles = UserRoles.ACADEMY

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_USER

  // ソート情報
  private sort: TeacherListSortType | null = null

  // 先生一覧表示情報
  private teacherDatas: TeacherListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // モーダルに表示する先生編集情報
  private teacherEditData: TeacherEditType | null = null

  // モーダルの教室選択オプション
  private modalBranchSelectOptions: InputItem[] = []

  // モーダルに表示するエラーメッセージ
  private modalErrorMessages: string[] = []

  // 現在の検索パラメーター
  private currentSearchParams: TeacherSearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 権限プレースホルダー
  private authorityPlaceholder: InputItem = ManagerInputUtils.buildPlaceholder(InputItemPlaceholderSetting.PRIORITY_ALL)

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    // 教室プルダウンのオプションを取得（編集モーダル内のプルダウンだが、どの編集対象でも同じオプションを使用するためここで取得）
    const apiResponse = await this.getBranches(this.userRole)
    this.modalBranchSelectOptions = ManagerInputUtils.buildBranchInputItems(
      ManagerInputUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_BRANCH),
      apiResponse
    )
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      userName: SortSettings.ASC,
      bcbtCode: SortSettings.ASC,
      branchName: SortSettings.ASC,
      roleName: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // 全チェックボックス、ソートを初期化
    this.template.checkedAll = false
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: TeacherListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 先生・講師検索API呼び出し
    this.currentSearchParams = {
      page: page,
      userName: this.template.fullName,
      academyName: this.template.academyName,
      branchName: this.template.branchName,
      role: this.template.authority,
      userNameSort: this.sort.userName.code,
      bcbtCodeSort: this.sort.bcbtCode.code,
      academyNameSort: this.sort.academyName?.code || null,
      branchNameSort: this.sort.branchName?.code || null,
      roleSort: this.sort.roleName.code,
    }
    const apiResponse = await this.searchManagers(this.currentSearchParams, this.userRole)
    this.teacherDatas = TeacherManagementUtils.formatTeacherDatas(apiResponse)
    this.pagenationStatus = TeacherManagementUtils.getPagenationStatus(apiResponse)
    // チェックボックスのチェック状態をセット
    this.setCheckedTeacherUserIds()
  }

  // チェックボックスのチェック状態をセットする処理
  private setCheckedTeacherUserIds() {
    if (this.template.checkedAll) {
      // 全チェックボックスがチェックされている場合、全データのチェックボックスをチェックする
      this.template.checkedTeacherUserIds = this.teacherDatas.map((teacherData) => {
        return teacherData.userId
      })
    } else {
      // チェックされていない場合は全てを未チェックにする
      this.template.checkedTeacherUserIds = []
    }
  }

  // QRコード出力イベント
  private async qrOutputEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedTeacherUserIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // 出力処理
    this.$loading.start()
    // チェックボックスがチェックされた管理者情報を絞り込みに使用する（全選択時は絞り込み不要）
    const userIds: number[] = this.template.checkedAll ? [] : this.template.checkedTeacherUserIds
    try {
      // 直近の検索パラメーターを用いてpdfデータを取得する
      const exportData = await this.downloadManagerQrLoginPdf(this.currentSearchParams, userIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = `manager_qr_${this.$localDateFns.formatTimestamp(new Date())}.pdf`
      fileLink.click()
      this.$loading.complete()
    } catch (e: any) {
      alert(e.response.data.message)
    }
  }

  // ダウンロードイベント
  private async downloadEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedTeacherUserIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // ダウンロード処理
    this.$loading.start()
    // チェックボックスがチェックされた管理者情報を絞り込みに使用する（全選択時は絞り込み不要）
    const userIds: number[] = this.template.checkedAll ? [] : this.template.checkedTeacherUserIds
    try {
      // 直近の検索パラメーターを用いてエクセルデータを取得する
      const exportData = await this.exportManagerExcel(this.currentSearchParams, userIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = '管理者ユーザ一覧.xlsx'
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 現ページのリンク押下時は遷移処理なし
    if (link === this.currentLink) return

    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Academy${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AcademyLogin' })
  }

  // 見出しタブ押下イベント
  private tabHeadingEvent(name: string) {
    // リンク先ページへ遷移
    this.$router.push({ name: `Academy${name}` })
  }

  // 編集データを取得して設定するイベント
  private async setEditDataEvent(id: number) {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    // 先生・講師情報取得API呼び出し
    const apiResponse = await this.getManagerData(id, this.userRole)
    this.teacherEditData = TeacherManagementUtils.buildAcademyTeacherEditData(apiResponse)
    this.$loading.complete()
  }

  // モーダルの保存ボタン押下時のイベント
  private async modalKeepButtonEvent(): Promise<void> {
    if (!this.teacherEditData) {
      return
    }
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    try {
      if (this.teacherEditData.userId) {
        // studentEditDataにIDがある場合は更新処理
        await this.updateManagerData(this.teacherEditData, this.userRole)
      }
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー発生時はエラーメッセージを設定する
      this.modalErrorMessages = [e.response.data.message]
    }
  }
}
</script>
